/**
 * validateRedirectUri — client-side mirror of the server-side
 * validateRedirectURIs rule (https-except-loopback).
 *
 * Rules (mirrors RFC 6749 §3.1.2 + OAuth 2.1 / Security BCP):
 *  - non-empty / non-whitespace
 *  - parseable as an absolute URL (has scheme + host)
 *  - scheme must be http or https
 *  - no fragment (url.hash non-empty OR raw contains '#')
 *  - http is only permitted for loopback hosts:
 *    localhost, 127.0.0.1, ::1 (also [::1] — browser URL API returns [::1])
 *
 * Returns null when valid, or a human-readable error string when invalid.
 * Pure function, no external dependencies.
 */
export function validateRedirectUri(raw: string): string | null {
  if (raw.trim() === '') {
    return 'Redirect URI is required'
  }

  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return 'Redirect URI must be an absolute URL'
  }

  // Must have a host (new URL('http:') doesn't throw but has no host).
  if (!url.host) {
    return 'Redirect URI must be an absolute URL'
  }

  const scheme = url.protocol.replace(/:$/, '').toLowerCase()

  if (scheme !== 'http' && scheme !== 'https') {
    return 'Redirect URI scheme must be http or https'
  }

  // Fragment is prohibited.
  if (url.hash !== '' || raw.includes('#')) {
    return 'Redirect URI must not contain a fragment'
  }

  // http is only allowed for loopback.
  if (scheme === 'http' && !isLoopbackHostname(url.hostname)) {
    return 'Redirect URI must use https (http allowed only for loopback)'
  }

  return null
}

/**
 * isLoopbackHostname checks whether a URL hostname (as returned by the
 * browser's URL API) is a loopback host.
 *
 * The browser URL API returns:
 *   - 'localhost'         for http://localhost/
 *   - '127.0.0.1'        for http://127.0.0.1/
 *   - '[::1]'            for http://[::1]/   (brackets preserved by browser)
 *   - '::1'              is NOT returned by the browser but handle defensively
 *
 * Port is NOT included in url.hostname (browser API strips it).
 */
function isLoopbackHostname(hostname: string): boolean {
  const h = hostname.toLowerCase()
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1'
}
